/**
 * Cube post-call wrap-up (ACW): loads disposition list and submits via connector.
 * Widget shows the disposition panel when wrap-up is active and Status is Disconnected.
 */
import { Injectable } from '@angular/core';
import {
  ensureDialersDispositionListReady,
  getDialersDispositionList,
} from '../../../helper/dialersConfigs/dialers-disposition-list';
import { CubeConnectorCoreService } from './cube-connector-core.service';
import { cubeDebugLog } from './cube-debug-log';

export interface CubeDispositionOption {
  id: string;
  reason: string;
  label: string;
}

@Injectable()
export class CubeWrapUpService {
  constructor(private cubeConnector: CubeConnectorCoreService) {}

  /** True while connector is waiting for disposition before next call. */
  isWrapUpActive(): boolean {
    return this.cubeConnector.isWrapUpPending();
  }

  /** Immediate list for the widget dropdown. Never wait on S3 before first paint. */
  loadDispositionsSync(): CubeDispositionOption[] {
    const reasons = getDialersDispositionList();
    const source = reasons.length ? reasons : this.localFallback();
    return source.map((reason) => ({
      id: reason,
      reason,
      label: reason.replace(/__/g, ' / '),
    }));
  }

  private localFallback(): string[] {
    return [
      'Account closed',
      'Auto Dispose',
      'Call back',
      'Call drop',
      'PTP',
      'Paid',
      'Not interested',
      'Not Reachable',
      'Ringing No Response',
      'Language barrier',
      'Need FollowUp',
    ];
  }

  async loadDispositions(): Promise<CubeDispositionOption[]> {
    cubeDebugLog('WrapUp', 'loadDispositions_start', {});
    const immediate = this.loadDispositionsSync();
    try {
      await ensureDialersDispositionListReady();
    } catch (_e) {}
    const list = this.loadDispositionsSync();
    cubeDebugLog('WrapUp', 'loadDispositions_done', { count: list.length });
    return list.length ? list : immediate;
  }

  /** Sends disposition + optional comment to iframe/connector; completes wrap-up on success. */
  async submitDisposition(
    reason: string,
    comment?: string
  ): Promise<{ success: boolean; message?: string }> {
    const disposition = String(reason || '').trim();
    if (!disposition) {
      return { success: false, message: 'Select a disposition' };
    }
    return this.cubeConnector.submitWrapUpDisposition(disposition, comment?.trim() || '');
  }
}
